import { AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import useAPI from "@toothfairy/shared-api/useApi";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowDimensions, View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import useFileSelector from "../../../../Hooks/useFileSelector";
import utils from "../../../Common/utils";
import AppTable from "@toothfairy/shared-ui/AppTable";
import trainingAPI from "../../../../API/Training/trainingAPI";
import Files from "../../../../API/Files";
import envConfig from "../../../../../envConfig";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const BENCHMARK_SECTION_FIELD_MAP = {
  profile: ["name", "description"],
  data: ["benchmarks", "files"],
};

const validateBenchmarkForABM = (questions) => {
  const questionCount = questions?.length || 0;

  const results = {
    supportsStandardBM: questionCount >= 1,
    supportsABMStatic: questionCount >= 10 && questionCount <= 200,
    supportsABMDynamic: questionCount >= 1,
    staticWarning: questionCount > 0 && questionCount < 10,
    staticMaxWarning: questionCount > 200,
    questionCount: questionCount,
  };

  return results;
};

const BenchmarkSettingsView = ({
  isEdit,
  settingType,
  handleDeletion,
  setDisplayTokenSection,
  displayTokenSection,
  requestValidation = () => {},
  onFilesUploaded = () => {},
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { height } = useWindowDimensions();
  const { currentTheme } = AppTheme.useTheme();
  const entityName = "Benchmark mapping";
  const columns = [
    {
      title: t("settings.benchmark.question"),
      width: 0.2,
    },
    {
      title: t("settings.benchmark.answer"),
      width: 0.2,
    },
    {
      title: t("settings.benchmark.reasoning"),
      width: 0.2,
    },
    {
      title: t("settings.benchmark.context"),
      width: 0.2,
    },
  ];
  const {
    setFieldValue,
    values,
    validationSchema,
    isValid,
    dirty,
    initialValues,
    errors,
    submitCount,
  } = AppForms.useFormikContext();
  const { state } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const {
    getWorkspaceBaseModel,
    getWorkspaceEntities,
    getActiveWorkspace,
    getPlanConfig,
  } = useWorkspaces();
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [filesUploadProgress, setFilesUploadProgress] = useState(0);
  const [presignedFilesUrls, setPresignedFilesUrls] = useState(null);
  const source = axios.CancelToken.source();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: BENCHMARK_SECTION_FIELD_MAP,
  });

  const validateBenchmarkCSV = (csvString) => {
    const MAX_FIELD_LENGTH = 6000;
    const errors = [];

    try {
      // Skip empty strings
      if (!csvString || csvString.trim() === "") {
        throw new Error(t("settings.benchmark.emptyFileContent"));
      }

      // Split the string by newlines and filter out empty lines
      const lines = csvString.split("\n").filter((row) => row.trim() !== "");

      lines.forEach((line, index) => {
        const lineNumber = index + 1;

        // Regular expression to match fields with proper text qualification
        const regex = /"([^"]*)"|([^;]+)/g;
        const fields = [];
        let match;

        // Extract all fields considering quoted content
        while ((match = regex.exec(line))) {
          // Get either the quoted content (match[1]) or non-quoted content (match[2])
          const value = (match[1] || match[2] || "").trim();
          if (value && value != "") fields.push(value);
        }
        // Validate number of fields (2 required + 1 optional)
        if (fields.length < 2 || fields.length > 4) {
          errors.push(
            t("settings.benchmark.invalidFieldCount", { lineNumber })
          );
          return;
        }

        const [question, answer, reasoning, context] = fields;

        // Validate required fields presence
        if (!question || question.trim() === "") {
          errors.push(t("settings.benchmark.questionRequired", { lineNumber }));
        }

        if (!answer || answer.trim() === "") {
          errors.push(t("settings.benchmark.answerRequired", { lineNumber }));
        }

        // Validate field lengths after removing quotes
        if (question.length > MAX_FIELD_LENGTH) {
          errors.push(
            t("settings.benchmark.questionExceedsMax", {
              lineNumber,
              max: MAX_FIELD_LENGTH,
            })
          );
        }

        if (answer.length > MAX_FIELD_LENGTH) {
          errors.push(
            t("settings.benchmark.answerExceedsMax", {
              lineNumber,
              max: MAX_FIELD_LENGTH,
            })
          );
        }

        if (reasoning && reasoning.length > MAX_FIELD_LENGTH) {
          errors.push(
            t("settings.benchmark.reasoningExceedsMax", {
              lineNumber,
              max: MAX_FIELD_LENGTH,
            })
          );
        }

        if (context && context.length > MAX_FIELD_LENGTH) {
          errors.push(
            t("settings.benchmark.contextExceedsMax", {
              lineNumber,
              max: MAX_FIELD_LENGTH,
            })
          );
        }

        // Additional validation for proper text qualification
        const quotedFieldsRegex = /^".*"$/;
        if (
          !quotedFieldsRegex.test(`"${question}"`) ||
          !quotedFieldsRegex.test(`"${answer}"`) ||
          (reasoning && !quotedFieldsRegex.test(`"${reasoning}"`)) ||
          (context && !quotedFieldsRegex.test(`"${context}"`))
        ) {
          errors.push(
            t("settings.benchmark.fieldsMustBeQuoted", { lineNumber })
          );
        }
      });

      return {
        isValid: errors.length === 0,
        errors,
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [
          t("settings.benchmark.fileParsingError", { message: error.message }),
        ],
      };
    }
  };
  const getUrl = async (path, type = "file") => {
    let initialPath = "";
    if (type == "file") {
      initialPath = "imported_doc_files";
    } else {
      return "";
    }
    const _url = await S3downloadUrlRequest({
      filename: `${initialPath}/${getActiveWorkspace()?.id}/${path}`,
      context: "pdf",
      workspaceid: getActiveWorkspace()?.id,
    });
    if (_url?.url && type == "file") return _url?.url;
  };
  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const handleBatchFilesSelection = async (files, type = "file") => {
    try {
      if (!files) return;
      console.log("files", files);
      const filesArray = Array.from(files);
      const uploadedFilesUrls = [];
      const presignedFilesUrls = [];

      // Initialize progress tracking
      setFileUploadInProgress(true);
      const initialProgress = filesArray.map((_, i) => ({
        id: i,
        progress: 0,
      }));
      setFilesUploadProgress(initialProgress);
      let totalProgress = 0;

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        let filename = file.name;
        filename = filename.replace(/[^a-zA-Z0-9.]/g, "");
        const _fileN = String(Date.now()) + filename;
        if (filename?.length > 100) {
          return AppToasty.show(t("settings.benchmark.fileNameTooLong"), {
            type: "danger",
          });
        }
        const contentType = filename.slice(
          (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1
        );
        let importType = "imported_doc_files";

        const result = await S3uploadUrlRequest({
          filename: `${getActiveWorkspace()?.id}/${_fileN}`,
          importType,
          contentType,
        });

        const upload = await axios.request({
          method: "put",
          url: JSON.parse(result?.body)?.uploadURL,
          data: file,
          headers: { "Content-Type": file.type },
          cancelToken: source.token,
          onUploadProgress: (p) => {
            const fileProgress = Math.round((p.loaded * 100) / p.total);

            setFilesUploadProgress((prevProgress) => {
              const newProgress = [...prevProgress];
              newProgress[i] = { ...newProgress[i], progress: fileProgress };
              totalProgress =
                newProgress.reduce((sum, file) => sum + file.progress, 0) /
                filesArray.length;
              setFilesUploadProgress(Math.round(totalProgress));
              return newProgress;
            });
          },
        });
        if (JSON.parse(result?.body).uploadURL) {
          const _url = await getUrl(_fileN, "file");
          uploadedFilesUrls.push(
            `imported_doc_files/${getActiveWorkspace()?.id}/${_fileN}`
          );
          presignedFilesUrls.push(_url);
        }
      }
      setFieldValue("files", uploadedFilesUrls);
      setPresignedFilesUrls(presignedFilesUrls);
      setFilesUploadProgress(100);
      if (type === "file") {
        onFilesUploaded(uploadedFilesUrls, presignedFilesUrls);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setFileUploadInProgress(false);
    } finally {
      setFileUploadInProgress(false);
    }
  };
  const isValidFileSize = (file, mb = 10) => {
    const maxSizeInBytes = mb * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };
  const handleFileUpload = (files) => {
    for (let i = 0; i < files.length; i++) {
      if (!isValidFileSize(files[i], 10)) {
        return AppToasty.show(
          t("settings.benchmark.fileSizeExceeded", { fileName: files[i].name }),
          {
            type: "danger",
          }
        );
      }
    }
    handleBatchFilesSelection(files, "file");
  };
  const {
    handleFileSelect: handleFileSelectGer_,
    filePath,
    handleImports: handleImportsGeneric,
    isFileUploadInProgress,
    batchUploadStatus,
  } = useFileSelector({
    _importType: "benchmark_ai",
    _importFile: "benchmark_ai",
    contentType: "benchmark_ai",
    additionalFilenameParams: ``,
    _appLogImportType: "documentImport",
    _appLogImportEvent: "bulkImportGenAi file import",
    showSuccessOnUpload: false,
    flat: false,
    createDocument: true,
    uploadOnSelection: true,
    showPreview: true,
    allowMultiple: false,
    onShowPreview: () => {
      console.log("onShowPreview");
    },
    onDocumentsCreated: (files) => {
      console.log("files", files);
    },
    onDocumentCreated: (file) => {
      console.log("file", file);
    },
    onFilePathChange: (file) => {
      console.log("file", file);
      handleFileUpload([file]);
    },
  });
  const _title = useCallback(() => {
    return isEdit ? t("settings.list.save") : t("settings.list.create");
  }, [entityName, isEdit, t]);

  const getBenchmarkObject = () => {
    return state?.benchmarks?.find((u) => u?.id === values?.id);
  };
  useEffect(() => {
    const pullSignedUrl = async () => {
      const _url = await S3downloadUrlRequest({
        filename: getBenchmarkObject()?.files[0],
        context: "pdf",
        workspaceid: getActiveWorkspace()?.id,
      });
      if (_url?.url) setPresignedFilesUrls([_url?.url]);
    };
    if (getBenchmarkObject()?.files?.length > 0) {
      pullSignedUrl();
    }
  }, [getBenchmarkObject()?.files?.length > 0]);
  useEffect(() => {
    if (getBenchmarkObject()?.questions?.length > 0) {
      console.log(
        "getBenchmarkObject()?.questions",
        getBenchmarkObject()?.questions
      );
      setFieldValue(
        "benchmarks",
        typeof getBenchmarkObject()?.questions == "string"
          ? JSON.parse(getBenchmarkObject()?.questions)?.map((u) => {
              return {
                question: u?.question,
                answer: u?.answer,
                reasoning: u?.reasoning,
                context: u?.context,
              };
            })
          : getBenchmarkObject()?.questions?.map((u) => {
              return {
                question: u?.question,
                answer: u?.answer,
                reasoning: u?.reasoning,
                context: u?.context,
              };
            })
      );
    }
  }, [getBenchmarkObject()?.questions?.length > 0]);
  const downloadCSVFromS3 = async (presignedUrl) => {
    try {
      // Fetch the file from the presigned URL
      const response = await fetch(presignedUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Extract filename from the presigned URL or use default
      const filename =
        presignedUrl.split("/").pop().split("?")[0] ||
        `download_${new Date().getTime()}.csv`;

      link.href = url;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      AppToasty.show(t("settings.benchmark.downloadError"), {
        type: "danger",
      });
    }
  };
  useEffect(() => {
    if (filePath) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const validations = validateBenchmarkCSV(text);
        if (validations?.errors?.length > 0) {
          AppToasty.show(validations?.errors?.join(", "), {
            type: "danger",
          });
          return;
        }

        const benchmarkArray = utils.parseCSVString(text);
        setFieldValue("benchmarks", benchmarkArray);
      };
      reader.onerror = (e) => {
        console.error(e);
        setFieldValue("benchmarks", []);
        AppToasty.show(t("settings.benchmark.fileReadError"), {
          type: "danger",
        });
      };
      reader.readAsText(filePath);
    }
  }, [filePath]);
  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("benchmarks.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          {isEdit && <IdCopyView id={getBenchmarkObject()?.id} />}
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            editable={!isEdit}
            wrapperInputStyle={{
              paddingTop: 10,
              marginRight: 15,
            }}
            label={t("settings.list.name")}
            placeholder={t("settings.benchmark.namePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            textInputLabelStyle={{
              marginLeft: 0,
            }}
            wrapperInputStyle={{
              height: 250,
              marginBottom: 0,
              padding: 10,
              marginLeft: 0,
            }}
            textContainer={{
              padding: 10,
              marginLeft: -5,
              minHeight: 200,
            }}
            multiline
            label={t("settings.list.description")}
            placeholder={t("settings.benchmark.descriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
          <View>
            <AppText
              preventTranslation
              isClickable
              color={
                presignedFilesUrls?.length > 0
                  ? currentTheme?.theme?.primary
                  : currentTheme?.theme?.darkGrey
              }
              leftIcon={AppAssets?.icons?.csv}
              iconSize={14}
              style={{
                fontWeight: "bold",
                marginLeft: 10,
              }}
              onPress={() => {
                handleFileSelectGer_();
              }}
              wrapperStyle={{
                marginLeft: 10,
              }}
            >
              {presignedFilesUrls?.length > 0
                ? t("settings.benchmark.changeFile")
                : t("settings.benchmark.uploadFile")}
            </AppText>
          </View>

          <AppText
            preventTranslation
            leftIcon={AppAssets?.icons?.info}
            wrapperStyle={{
              paddingHorizontal: 10,
              marginTop: 10,
              marginLeft: 0,
            }}
            color={currentTheme?.theme?.orange}
            style={{
              marginLeft: 10,
              textDecorationColor: currentTheme?.theme?.orange,
            }}
            iconSize={14}
            isClickable
            onPress={() => {
              window.open(
                `https://docs.toothfairyai.com/docs/Settings/benchmarks`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
          >
            {t("settings.benchmark.fileStructureHelp")}
          </AppText>
        </SectionRenderer>
      )}
      {_showBuilderSection("data") && values?.benchmarks?.length > 0 && (
        <SectionRenderer
          title={t("benchmarks.builderSectionData", {
            defaultValue: "Data",
          })}
          icon={AppAssets.icons.benchmark}
        >
          <View
            style={{
              paddingHorizontal: 10,
              marginTop: 15,
              marginBottom: 10,
            }}
          >
            <AppText
              preventTranslation
              style={{
                fontWeight: "bold",
                fontSize: 14,
                marginBottom: 8,
              }}
            >
              {t("settings.benchmark.compatibility")}
            </AppText>
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginRight: 15,
                }}
              >
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 12,
                    color:
                      values?.benchmarks?.length >= 1
                        ? currentTheme?.theme?.green
                        : currentTheme?.theme?.red,
                  }}
                >
                  {values?.benchmarks?.length >= 1 ? "✓" : "✗"}{" "}
                  {t("settings.benchmark.bmStandard")}
                </AppText>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginRight: 15,
                }}
              >
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 12,
                    color:
                      values?.benchmarks?.length >= 10 &&
                      values?.benchmarks?.length <= 200
                        ? currentTheme?.theme?.green
                        : values?.benchmarks?.length > 200
                        ? currentTheme?.theme?.orange
                        : currentTheme?.theme?.red,
                  }}
                >
                  {values?.benchmarks?.length >= 10 &&
                  values?.benchmarks?.length <= 200
                    ? "✓"
                    : values?.benchmarks?.length > 200
                    ? "⚠"
                    : "✗"}{" "}
                  {t("settings.benchmark.abmStatic")}
                </AppText>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 12,
                    color:
                      values?.benchmarks?.length >= 1
                        ? currentTheme?.theme?.green
                        : currentTheme?.theme?.red,
                  }}
                >
                  {values?.benchmarks?.length >= 1 ? "✓" : "✗"}{" "}
                  {t("settings.benchmark.abmDynamic")}
                </AppText>
              </View>
            </View>
            {values?.benchmarks?.length === 0 && (
              <AppText
                preventTranslation
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.red,
                  marginTop: 8,
                }}
              >
                {t("settings.benchmark.uploadFileToEnable")}
              </AppText>
            )}
            {values?.benchmarks?.length > 0 &&
              values?.benchmarks?.length < 10 && (
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 11,
                    color: currentTheme?.theme?.orange,
                    marginTop: 8,
                  }}
                >
                  {t("settings.benchmark.abmStaticRecommended", {
                    count: values?.benchmarks?.length,
                  })}
                </AppText>
              )}
            {values?.benchmarks?.length > 200 && (
              <AppText
                preventTranslation
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.orange,
                  marginTop: 8,
                }}
              >
                {t("settings.benchmark.abmStaticMax", {
                  count: values?.benchmarks?.length,
                })}
              </AppText>
            )}
          </View>

          <AppTable
            style={{
              zIndex: -1,
              height:
                values?.benchmarks?.length >= 50 ? height - 95 : height - 75,
              paddingRight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
            headers={columns}
            items={
              values?.benchmarks?.slice(0, 10)?.map((u) => {
                return {
                  question: u?.question || "",
                  answer: u?.answer || "",
                  reasoning: u?.reasoning || "",
                  context: u?.context || "",
                };
              }) || []
            }
          />
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 10,
              paddingHorizontal: 10,
            }}
          >
            {presignedFilesUrls?.length > 0 && (
              <AppText
                preventTranslation
                isClickable={true}
                color={currentTheme?.theme?.primary}
                style={{
                  fontWeight: "bold",
                }}
                onPress={() => {
                  // create download method file for presignedFilesUrls
                  downloadCSVFromS3(presignedFilesUrls[0]);
                }}
              >
                {t("settings.benchmark.downloadCSV")}
              </AppText>
            )}
            <AppText
              preventTranslation
              wrapperStyle={{
                justifyContent: "flex-end",
              }}
              style={{
                fontWeight: "bold",
              }}
            >
              {`${t("settings.benchmark.totalRecords")}: ${
                values?.benchmarks?.length || 0
              }${
                values?.benchmarks?.length > 10
                  ? `, ${t("settings.benchmark.showingFirst10")}`
                  : ""
              }`}
            </AppText>
          </View>
        </SectionRenderer>
      )}

      {!isBuilderLayout && (
        <View style={{ alignItems: "center", zIndex: -1 }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />

          {isEdit && (
            <ToothFairyButton
              title={t("settings.list.delete")}
              onPress={() => handleDeletion(getBenchmarkObject()?.id)}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default BenchmarkSettingsView;
